import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api/api.service';

export interface NotificationItem {
    id: string;
    org_id: string | null;
    user_id: string;
    type: string;
    title: string | null;
    body: string | null;
    deep_link: string | null;
    data: Record<string, unknown> | null;
    actor_user_id: string | null;
    priority: 'low' | 'normal' | 'high';
    expires_at: string | null;
    icon: string | null;
    read_at: string | null;
    push_status: string | null;
    push_attempts: number | null;
    last_push_error: string | null;
    pushed_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface NotificationListResponse {
    items: NotificationItem[];
    limit: number;
    offset: number;
}

export interface UnreadCountResponse {
    unread: number;
}

export interface RegisterTokenResponse {
    id: string;
    user_id: string;
    org_id: string | null;
    token: string;
    device_id: string | null;
    platform: string | null;
    device_label: string | null;
    app_version: string | null;
    last_seen_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface NotificationTypeStat {
    type: string;
    label: string;
    icon: string;
    count: number;
    unread: number;
}

export interface NotificationPreferenceItem {
    type: string;
    label: string;
    icon: string;
    push_enabled: boolean;
    in_app_enabled: boolean;
    email_enabled: boolean;
}

export interface NotificationPreferencesResponse {
    items: NotificationPreferenceItem[];
}

export interface NotificationListOptions {
    limit?: number;
    offset?: number;
    include_read?: boolean;
    unread_only?: boolean;
    type?: string | null;
}

// ─── Admin (app_admin only) ────────────────────────────────────────────────

export type NotificationAudience = 'all' | 'org' | 'users';
export type NotificationPriority = 'low' | 'normal' | 'high';

export interface AnnouncePayload {
    title: string;
    body?: string | null;
    audience?: NotificationAudience;
    org_id?: string | null;
    user_ids?: string[];
    priority?: NotificationPriority;
    deep_link?: string | null;
    push_immediately?: boolean | null;
}

export interface AnnounceResponse {
    ok: boolean;
    announcement_id: string;
    audience: NotificationAudience;
    recipients: number;
    inserted: number;
    pushed: number;
    push_attempts: number;
    push_deferred: boolean;
    unresolved_emails: string[];
    push_errors: { id: string; error: string }[];
}

export interface AnnouncementRow {
    announcement_id: string;
    title: string | null;
    body: string | null;
    actor_user_id: string | null;
    priority: NotificationPriority;
    recipient_count: number;
    pending_pushes: number;
    sent_pushes: number;
    failed_pushes: number;
    skipped_pushes: number;
    created_at: string | null;
}

export interface AdminNotificationItem extends NotificationItem {
    user_email: string | null;
    user_name: string | null;
}

export interface AdminNotificationListOptions {
    user_id?: string | null;
    org_id?: string | null;
    type?: string | null;
    push_status?: string | null;
    priority?: string | null;
    q?: string | null;
    from?: string | null;
    to?: string | null;
    limit?: number;
    offset?: number;
}

export interface AdminNotificationListResponse {
    items: AdminNotificationItem[];
    limit: number;
    offset: number;
}

export interface AdminTokenRow {
    id: string;
    user_id: string;
    org_id: string | null;
    token: string;
    device_id: string | null;
    platform: string | null;
    device_label: string | null;
    app_version: string | null;
    last_seen_at: string | null;
    created_at: string;
    updated_at: string;
    user_email: string | null;
    user_name: string | null;
    org_name: string | null;
}

export interface AdminTokenListOptions {
    user_id?: string | null;
    org_id?: string | null;
    platform?: string | null;
    stale?: boolean | null;
    limit?: number;
    offset?: number;
}

export interface AdminTokenListResponse {
    items: AdminTokenRow[];
    limit: number;
    offset: number;
}

export interface PushDiagnostics {
    by_push_status: Record<string, number>;
    by_priority: Record<string, number>;
    tokens_by_platform: { platform: string; count: number }[];
    total_tokens: number;
    stale_tokens: number;
    recent_failed: {
        id: string;
        user_id: string;
        user_email: string | null;
        type: string;
        title: string | null;
        push_attempts: number;
        last_push_error: string | null;
        created_at: string | null;
    }[];
}

export interface NotificationTypeDefinition {
    type: string;
    label: string;
    icon: string;
}

export interface NotificationTypeSetting {
    type: string;
    org_id: string | null;
    org_name: string | null;
    enabled: boolean;
    updated_at: string | null;
}

@Injectable({ providedIn: 'root' })
export class NotificationsApiService {
    private api = inject(ApiService);

    registerToken(payload: {
        token: string;
        device_id?: string | null;
        platform?: 'web' | 'android' | 'ios' | null;
        device_label?: string | null;
        app_version?: string | null;
        app_user_agent?: string | null;
    }): Observable<RegisterTokenResponse> {
        return this.api.post<any>('/API2/notifications/register-token', payload).pipe(
            map((resp) => resp?.data ?? resp)
        );
    }

    unregisterToken(token: string): Observable<{ removed: number }> {
        return this.api.delete<any>('/API2/notifications/register-token', { body: { token } }).pipe(
            map((resp) => resp?.data ?? resp ?? { removed: 0 })
        );
    }

    touchToken(token: string): Observable<{ ok: boolean }> {
        return this.api.post<any>('/API2/notifications/touch-token', { token }).pipe(
            map((resp) => resp?.data ?? resp ?? { ok: true })
        );
    }

    list(options: NotificationListOptions = {}): Observable<NotificationListResponse> {
        let params = new HttpParams();
        const limit = options.limit ?? 50;
        const offset = options.offset ?? 0;
        params = params.set('limit', String(limit)).set('offset', String(offset));
        if (options.include_read === false) params = params.set('include_read', 'false');
        if (options.unread_only) params = params.set('unread_only', 'true');
        if (options.type) params = params.set('type', String(options.type));
        return this.api.get<any>('/API2/notifications', params).pipe(
            map((resp) => {
                const items = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp?.data) ? resp.data : []);
                const normalize = (r: any): NotificationItem => ({
                    id: String(r?.id ?? ''),
                    org_id: r?.org_id ? String(r.org_id) : null,
                    user_id: r?.user_id ? String(r.user_id) : '',
                    type: r?.type ? String(r.type) : '',
                    title: r?.title ?? null,
                    body: r?.body ?? null,
                    deep_link: r?.deep_link ?? null,
                    data: r?.data ?? null,
                    actor_user_id: r?.actor_user_id ? String(r.actor_user_id) : null,
                    priority: r?.priority ?? 'normal',
                    expires_at: r?.expires_at ?? null,
                    icon: r?.icon ?? null,
                    read_at: r?.read_at ?? null,
                    push_status: r?.push_status ?? null,
                    push_attempts: r?.push_attempts ?? null,
                    last_push_error: r?.last_push_error ?? null,
                    pushed_at: r?.pushed_at ?? null,
                    created_at: r?.created_at ?? '',
                    updated_at: r?.updated_at ?? ''
                });
                return {
                    items: items.map(normalize),
                    limit: Number(resp?.limit ?? limit),
                    offset: Number(resp?.offset ?? offset)
                };
            })
        );
    }

    unreadCount(): Observable<number> {
        return this.api.get<any>('/API2/notifications/unread-count').pipe(
            map((resp) => Number(resp?.unread ?? resp?.data?.unread ?? 0))
        );
    }

    types(): Observable<NotificationTypeStat[]> {
        return this.api.get<any>('/API2/notifications/types').pipe(
            map((resp) => {
                const items = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp?.data) ? resp.data : []);
                return items.map((r: any) => ({
                    type: String(r?.type ?? ''),
                    label: String(r?.label ?? r?.type ?? ''),
                    icon: String(r?.icon ?? 'bell'),
                    count: Number(r?.count ?? 0),
                    unread: Number(r?.unread ?? 0)
                }));
            })
        );
    }

    getPreferences(): Observable<NotificationPreferenceItem[]> {
        return this.api.get<any>('/API2/notifications/preferences').pipe(
            map((resp) => {
                const items = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp?.data) ? resp.data : []);
                return items.map((r: any) => ({
                    type: String(r?.type ?? ''),
                    label: String(r?.label ?? r?.type ?? ''),
                    icon: String(r?.icon ?? 'bell'),
                    push_enabled: r?.push_enabled !== false,
                    in_app_enabled: r?.in_app_enabled !== false,
                    email_enabled: r?.email_enabled !== false
                }));
            })
        );
    }

    updatePreferences(items: Partial<NotificationPreferenceItem>[]): Observable<{ updated: number }> {
        return this.api.put<any>('/API2/notifications/preferences', { items }).pipe(
            map((resp) => resp?.data ?? resp ?? { updated: 0 })
        );
    }

    markRead(id: string): Observable<{ id: string; read_at: string | null }> {
        return this.api.patch<any>(`/API2/notifications/${encodeURIComponent(id)}/read`, {}).pipe(
            map((resp) => resp?.data ?? resp ?? {})
        );
    }

    markAllRead(): Observable<{ updated: number }> {
        return this.api.post<any>('/API2/notifications/read-all', {}).pipe(
            map((resp) => resp?.data ?? resp ?? { updated: 0 })
        );
    }

    delete(id: string): Observable<{ removed: number }> {
        return this.api.delete<any>(`/API2/notifications/${encodeURIComponent(id)}`).pipe(
            map((resp) => resp?.data ?? resp ?? { removed: 1 })
        );
    }

    // ─── Admin (app_admin only) ────────────────────────────────────────────

    adminAnnounce(payload: AnnouncePayload): Observable<AnnounceResponse> {
        return this.api.post<any>('/API2/admin/notifications/announce', payload).pipe(
            map((resp) => resp?.data ?? resp)
        );
    }

    adminListAnnouncements(limit = 50, offset = 0): Observable<{ items: AnnouncementRow[]; limit: number; offset: number }> {
        let params = new HttpParams().set('limit', String(limit)).set('offset', String(offset));
        return this.api.get<any>('/API2/admin/notifications/announcements', params).pipe(
            map((resp) => {
                const items = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp?.data) ? resp.data : []);
                return {
                    items: items.map((r: any): AnnouncementRow => ({
                        announcement_id: String(r?.announcement_id ?? ''),
                        title: r?.title ?? null,
                        body: r?.body ?? null,
                        actor_user_id: r?.actor_user_id ?? null,
                        priority: r?.priority ?? 'normal',
                        recipient_count: Number(r?.recipient_count ?? 0),
                        pending_pushes: Number(r?.pending_pushes ?? 0),
                        sent_pushes: Number(r?.sent_pushes ?? 0),
                        failed_pushes: Number(r?.failed_pushes ?? 0),
                        skipped_pushes: Number(r?.skipped_pushes ?? 0),
                        created_at: r?.created_at ?? null
                    })),
                    limit: Number(resp?.limit ?? limit),
                    offset: Number(resp?.offset ?? offset)
                };
            })
        );
    }

    adminList(options: AdminNotificationListOptions = {}): Observable<AdminNotificationListResponse> {
        let params = new HttpParams();
        const limit = options.limit ?? 50;
        const offset = options.offset ?? 0;
        params = params.set('limit', String(limit)).set('offset', String(offset));
        if (options.user_id) params = params.set('user_id', String(options.user_id));
        if (options.org_id) params = params.set('org_id', String(options.org_id));
        if (options.type) params = params.set('type', String(options.type));
        if (options.push_status) params = params.set('push_status', String(options.push_status));
        if (options.priority) params = params.set('priority', String(options.priority));
        if (options.q) params = params.set('q', String(options.q));
        if (options.from) params = params.set('from', String(options.from));
        if (options.to) params = params.set('to', String(options.to));
        return this.api.get<any>('/API2/admin/notifications', params).pipe(
            map((resp) => {
                const items = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp?.data) ? resp.data : []);
                const normalize = (r: any): AdminNotificationItem => ({
                    id: String(r?.id ?? ''),
                    org_id: r?.org_id ? String(r.org_id) : null,
                    user_id: r?.user_id ? String(r.user_id) : '',
                    type: r?.type ? String(r.type) : '',
                    title: r?.title ?? null,
                    body: r?.body ?? null,
                    deep_link: r?.deep_link ?? null,
                    data: r?.data ?? null,
                    actor_user_id: r?.actor_user_id ? String(r.actor_user_id) : null,
                    priority: r?.priority ?? 'normal',
                    expires_at: r?.expires_at ?? null,
                    icon: r?.icon ?? null,
                    read_at: r?.read_at ?? null,
                    push_status: r?.push_status ?? null,
                    push_attempts: r?.push_attempts ?? null,
                    last_push_error: r?.last_push_error ?? null,
                    pushed_at: r?.pushed_at ?? null,
                    created_at: r?.created_at ?? '',
                    updated_at: r?.updated_at ?? '',
                    user_email: r?.user_email ?? null,
                    user_name: r?.user_name ?? null
                });
                return {
                    items: items.map(normalize),
                    limit: Number(resp?.limit ?? limit),
                    offset: Number(resp?.offset ?? offset)
                };
            })
        );
    }

    adminDelete(id: string): Observable<{ removed: number }> {
        return this.api.delete<any>(`/API2/admin/notifications/${encodeURIComponent(id)}`).pipe(
            map((resp) => resp?.data ?? resp ?? { removed: 1 })
        );
    }

    adminRetryPush(id: string): Observable<{ id: string; push_status: string; push_attempts: number }> {
        return this.api.post<any>(`/API2/admin/notifications/${encodeURIComponent(id)}/retry-push`, {}).pipe(
            map((resp) => resp?.data ?? resp)
        );
    }

    adminListTokens(options: AdminTokenListOptions = {}): Observable<AdminTokenListResponse> {
        let params = new HttpParams();
        const limit = options.limit ?? 50;
        const offset = options.offset ?? 0;
        params = params.set('limit', String(limit)).set('offset', String(offset));
        if (options.user_id) params = params.set('user_id', String(options.user_id));
        if (options.org_id) params = params.set('org_id', String(options.org_id));
        if (options.platform) params = params.set('platform', String(options.platform));
        if (options.stale) params = params.set('stale', 'true');
        return this.api.get<any>('/API2/admin/notifications/tokens', params).pipe(
            map((resp) => {
                const items = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp?.data) ? resp.data : []);
                const normalize = (r: any): AdminTokenRow => ({
                    id: String(r?.id ?? ''),
                    user_id: r?.user_id ? String(r.user_id) : '',
                    org_id: r?.org_id ? String(r.org_id) : null,
                    token: String(r?.token ?? ''),
                    device_id: r?.device_id ?? null,
                    platform: r?.platform ?? null,
                    device_label: r?.device_label ?? null,
                    app_version: r?.app_version ?? null,
                    last_seen_at: r?.last_seen_at ?? null,
                    created_at: r?.created_at ?? '',
                    updated_at: r?.updated_at ?? '',
                    user_email: r?.user_email ?? null,
                    user_name: r?.user_name ?? null,
                    org_name: r?.org_name ?? null
                });
                return {
                    items: items.map(normalize),
                    limit: Number(resp?.limit ?? limit),
                    offset: Number(resp?.offset ?? offset)
                };
            })
        );
    }

    adminDeleteToken(id: string): Observable<{ removed: number }> {
        return this.api.delete<any>(`/API2/admin/notifications/tokens/${encodeURIComponent(id)}`).pipe(
            map((resp) => resp?.data ?? resp ?? { removed: 1 })
        );
    }

    adminDiagnostics(): Observable<PushDiagnostics> {
        return this.api.get<any>('/API2/admin/notifications/diagnostics').pipe(
            map((resp) => {
                const d = resp?.data ?? resp ?? {};
                return {
                    by_push_status: d.by_push_status ?? {},
                    by_priority: d.by_priority ?? {},
                    tokens_by_platform: Array.isArray(d.tokens_by_platform) ? d.tokens_by_platform : [],
                    total_tokens: Number(d.total_tokens ?? 0),
                    stale_tokens: Number(d.stale_tokens ?? 0),
                    recent_failed: Array.isArray(d.recent_failed) ? d.recent_failed.map((r: any) => ({
                        id: String(r?.id ?? ''),
                        user_id: r?.user_id ? String(r.user_id) : '',
                        user_email: r?.user_email ?? null,
                        type: r?.type ? String(r.type) : '',
                        title: r?.title ?? null,
                        push_attempts: Number(r?.push_attempts ?? 0),
                        last_push_error: r?.last_push_error ?? null,
                        created_at: r?.created_at ?? null
                    })) : []
                };
            })
        );
    }

    adminListTypes(): Observable<NotificationTypeDefinition[]> {
        return this.api.get<any>('/API2/admin/notifications/types').pipe(
            map((resp) => {
                const items = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp?.data) ? resp.data : []);
                return items.map((r: any) => ({
                    type: String(r?.type ?? ''),
                    label: String(r?.label ?? r?.type ?? ''),
                    icon: String(r?.icon ?? 'bell')
                }));
            })
        );
    }

    adminListTypeSettings(): Observable<NotificationTypeSetting[]> {
        return this.api.get<any>('/API2/admin/notifications/type-settings').pipe(
            map((resp) => {
                const items = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp?.data) ? resp.data : []);
                return items.map((r: any) => ({
                    type: String(r?.type ?? ''),
                    org_id: r?.org_id ? String(r.org_id) : null,
                    org_name: r?.org_name ?? null,
                    enabled: r?.enabled !== false,
                    updated_at: r?.updated_at ?? null
                }));
            })
        );
    }

    adminUpdateTypeSetting(payload: { type: string; enabled: boolean; org_id?: string | null }): Observable<NotificationTypeSetting> {
        return this.api.put<any>('/API2/admin/notifications/type-settings', payload).pipe(
            map((resp) => {
                const r = resp?.data ?? resp ?? {};
                return {
                    type: String(r?.type ?? payload.type),
                    org_id: r?.org_id ? String(r.org_id) : null,
                    org_name: r?.org_name ?? null,
                    enabled: r?.enabled !== false,
                    updated_at: r?.updated_at ?? null
                };
            })
        );
    }
}
